// src/hooks/useOnlineStatus.js
// Hook for tracking online/offline status in React components

import { useState, useEffect, useCallback } from "react";

/**
 * Custom hook to track online/offline status
 * @returns {Object} - { isOnline, wasOffline, checkConnection }
 */
export default function useOnlineStatus() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [wasOffline, setWasOffline] = useState(false);

  // Handle online event
  const handleOnline = useCallback(() => {
    setIsOnline(true);
    // Track that we were offline (useful for showing "back online" messages)
    if (!isOnline) {
      setWasOffline(true);
      // Clear the wasOffline flag after 5 seconds
      setTimeout(() => setWasOffline(false), 5000);
    }
  }, [isOnline]);

  // Handle offline event
  const handleOffline = useCallback(() => {
    setIsOnline(false);
  }, []);

  // Manual connection check (useful for verifying actual connectivity)
  const checkConnection = useCallback(async () => {
    try {
      const response = await fetch("/", {
        method: "HEAD",
        cache: "no-store",
      });
      const online = response.ok;
      setIsOnline(online);
      return online;
    } catch {
      setIsOnline(false);
      return false;
    }
  }, []);

  useEffect(() => {
    // Set initial state
    setIsOnline(navigator.onLine);

    // Add event listeners
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    // Cleanup
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [handleOnline, handleOffline]);

  return {
    isOnline,
    wasOffline,
    checkConnection,
  };
}
